import { useCallback, useEffect, useState } from "react";
import { fetchBranchLicenseDetail, getLicenseExpiryDateString, normalizeLicenseApiResponse, normalizeRecordId } from "../../../libraries/licenseExpiry";
import { ensureProductionApiToken } from "../../../libraries/auth";

function LicenseValidity({ companyId, branchId, daysOutPut, onExpired, cachedData }) {
  const [licenceInfo, setLicenceInfo] = useState("Loading...");
  const [licenceStyle, setLicenceStyle] = useState("");

  // Process license data (used by both cached and fetched data)
  const processLicenseData = useCallback((responseData) => {
    const licenseDetail = normalizeLicenseApiResponse(responseData) || responseData;
    const expiryDate = getLicenseExpiryDateString(licenseDetail || responseData);

    // Check if response has expiryDate
    if (!expiryDate) {
      setLicenceInfo("License has Expired.");
      setLicenceStyle("licenceExpired");
      if (onExpired && branchId) {
        onExpired(branchId);
      }
      if (daysOutPut) {
        daysOutPut(-1); // Set negative days to indicate expired
      }
      return;
    }

    const now = new Date().getTime();
    const futureDate = new Date(expiryDate).getTime();

    // Check if date is valid
    if (isNaN(futureDate)) {
      setLicenceInfo("License has Expired.");
      setLicenceStyle("licenceExpired");
      if (onExpired && branchId) {
        onExpired(branchId);
      }
      if (daysOutPut) {
        daysOutPut(-1);
      }
      return;
    }

    const timeleft = futureDate - now;
    const days = Math.floor(timeleft / (1000 * 60 * 60 * 24));

    (days < 0) ? setLicenceInfo("License has Expired.") : setLicenceInfo("License Will Expire In : (Days " + days + ")");

    if (days < 0) {
      setLicenceStyle("licenceExpired");
      if (onExpired && branchId) {
        onExpired(branchId);
      }
    }
    else if (days >= 0 && days <= 5) {
      setLicenceStyle("aboutToExpire");
    } else {
      setLicenceStyle("");
    }

    if (daysOutPut) {
      daysOutPut(days);
    }
  }, [onExpired, branchId, daysOutPut]);

  useEffect(() => {
    const normalizedCompanyId = normalizeRecordId(companyId);
    const normalizedBranchId = normalizeRecordId(branchId);

    if (!normalizedCompanyId || !normalizedBranchId) {
      setLicenceInfo("N/A");
      setLicenceStyle("");
      return;
    }

    // Use cached data if available (from parent component)
    if (cachedData) {
      processLicenseData(cachedData);
      return;
    }

    // Fetch if not cached
    setLicenceInfo("Loading...");
    setLicenceStyle("");

    ensureProductionApiToken()
      .then(() => fetchBranchLicenseDetail({
        _id: normalizedBranchId,
        company: normalizedCompanyId
      }))
      .then((responseData) => {
        processLicenseData(responseData);
      })
      .catch(() => {
        // On error, assume expired to be safe
        setLicenceInfo("License has Expired.");
        setLicenceStyle("licenceExpired");
        if (onExpired && branchId) {
          onExpired(branchId);
        }
        if (daysOutPut) {
          daysOutPut(-1); // Set negative days to indicate expired
        }
      });
  }, [companyId, branchId, cachedData, onExpired, daysOutPut, processLicenseData]);

  const isExpired = licenceStyle === "licenceExpired";

  return (
    <div
      className={licenceStyle}
      style={isExpired ? {
        color: '#d51717',
        fontWeight: 'bold',
        display: 'inline-block',
        visibility: 'visible',
        opacity: 1
      } : {
        display: 'inline-block',
        visibility: 'visible',
        opacity: 1
      }}
    >
      {licenceInfo || 'Loading...'}
    </div>
  );
}

export default LicenseValidity;
